import { useState } from 'react';
import { Card, CardDescription, CardTitle } from '@/components/ui/card';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { Button } from '@/components/ui/button';
import { useSession } from '@/lib/session';
import { cn } from '@/lib/utils';
import { useRubric, useRubricVersions, useSaveRubric } from '../lib/api';
import type { RubricDto, RubricUpdateRequest, SubSegment } from '../types';
import { SUB_SEGMENT_LABELS, fmtDate } from './_shared';

// Phase D — the thesis-rubric editor, its own admin-only tab (MnaApp hides it for
// non-admins exactly like Debug; MNA.Api re-checks via the BFF trusted header).
// The rubric is versioned config (ADR-0009 §3): saving never edits v(N) in place —
// the server assigns v(N+1), marks it active and queues every company for re-score.
// The editor mirrors the server-side invariant client-side: dimension weights must
// sum to 1.0, shown as a live running total; Save stays disabled while invalid.

const SUB_SEGMENTS = Object.keys(SUB_SEGMENT_LABELS) as SubSegment[];

const fieldClass =
  'bg-surface-elevated border border-border rounded-(--radius-sm) px-3 py-2 text-sm ' +
  'font-(family-name:--font-body) text-text focus:outline-2 focus:outline-offset-2 focus:outline-accent';

export default function Rubric() {
  const { data: session } = useSession();
  const isAdmin = !!session?.isAdmin;

  if (!isAdmin) {
    return (
      <Card>
        <CardTitle>Rubric</CardTitle>
        <CardDescription className="mt-2">The thesis-rubric editor is admin-only.</CardDescription>
      </Card>
    );
  }
  return <RubricAdmin />;
}

function RubricAdmin() {
  const rubric = useRubric();
  const versions = useRubricVersions();
  // Survives the post-save remount of the keyed form below.
  const [savedNotice, setSavedNotice] = useState<string | null>(null);

  return (
    <>
      <Card>
        <CardTitle>Thesis rubric</CardTitle>
        <CardDescription className="mt-2">
          The versioned scoring config behind every fit score. Saving creates a new active version —
          it never rewrites history. <span className="text-accent">Admin-only</span>
        </CardDescription>
      </Card>

      {savedNotice && (
        <Card>
          <p className="text-sm text-score-high" role="status">
            {savedNotice}
          </p>
        </Card>
      )}

      {rubric.isLoading && <LoadingSkeleton lines={8} />}
      {rubric.error && <ErrorState message={(rubric.error as Error).message} />}
      {rubric.data && (
        // key = version: a successful save invalidates + refetches the active rubric;
        // remount re-seeds the form from the fresh server truth.
        <RubricForm
          key={rubric.data.version}
          rubric={rubric.data}
          onSaved={(v) =>
            setSavedNotice(`Saved as ${v}. All companies queued for re-score (next fit-score run applies ${v}).`)
          }
        />
      )}

      <VersionsList versions={versions.data} isLoading={versions.isLoading} error={versions.error as Error | null} />
    </>
  );
}

/* ---------- editor form ---------- */

function RubricForm({ rubric, onSaved }: { rubric: RubricDto; onSaved: (version: string) => void }) {
  const save = useSaveRubric();

  // Weights live as strings so intermediate keystrokes ("0.", "") don't fight the input.
  const [weights, setWeights] = useState<Record<string, string>>(() =>
    Object.fromEntries(rubric.dimensions.map((d) => [d.key, String(d.weight)]))
  );
  const [segWeights, setSegWeights] = useState<Record<SubSegment, string>>(
    () =>
      Object.fromEntries(
        SUB_SEGMENTS.map((s) => [s, String(rubric.subSegmentWeights[s] ?? 0)])
      ) as Record<SubSegment, string>
  );
  const [mustHaves, setMustHaves] = useState(rubric.mustHaves.join('\n'));
  const [exclusions, setExclusions] = useState(rubric.exclusions.join('\n'));
  const [note, setNote] = useState('');

  const parsed = rubric.dimensions.map((d) => Number.parseFloat(weights[d.key] ?? ''));
  const allValid = parsed.every((w) => Number.isFinite(w) && w >= 0);
  const total = parsed.reduce((s, w) => s + (Number.isFinite(w) ? w : 0), 0);
  const sumsToOne = Math.abs(total - 1) < 0.0005;
  const canSave = allValid && sumsToOne && !save.isPending;

  const segParsed = SUB_SEGMENTS.map((s) => Number.parseFloat(segWeights[s] ?? ''));
  const segTotal = segParsed.reduce((s, w) => s + (Number.isFinite(w) ? w : 0), 0);

  const splitLines = (v: string) => v.split('\n').map((l) => l.trim()).filter(Boolean);

  const onSave = () => {
    const body: RubricUpdateRequest = {
      dimensions: rubric.dimensions.map((d) => ({ ...d, weight: Number.parseFloat(weights[d.key]) })),
      subSegmentWeights: Object.fromEntries(
        SUB_SEGMENTS.map((s) => [s, Number.parseFloat(segWeights[s]) || 0])
      ) as Record<SubSegment, number>,
      mustHaves: splitLines(mustHaves),
      exclusions: splitLines(exclusions),
      note: note.trim() || null,
    };
    save.mutate(body, { onSuccess: (created) => onSaved(created.version) });
  };

  return (
    <>
      <Card>
        <div className="flex flex-wrap items-baseline gap-3">
          <span className="eyebrow">Active rubric</span>
          <span className="font-(family-name:--font-ui) text-sm font-semibold text-accent">{rubric.version}</span>
          {rubric.createdAt && <span className="text-xs text-muted">created {fmtDate(rubric.createdAt)}</span>}
        </div>

        {/* dimensions */}
        <table className="mt-4 w-full text-sm">
          <thead>
            <tr className="border-b-2 border-accent">
              <Th>Dimension</Th>
              <Th right>Weight</Th>
            </tr>
          </thead>
          <tbody>
            {rubric.dimensions.map((d) => (
              <tr key={d.key} className="border-b border-border align-top">
                <td className="py-3 px-3">
                  <span className="font-medium text-text">{d.label}</span>
                  <span className="ml-2 font-mono text-xs text-subtle">{d.key}</span>
                  {/* definition — read-only, expandable */}
                  <details className="mt-1">
                    <summary className="cursor-pointer text-xs text-muted hover:text-text">definition</summary>
                    <p className="mt-1 max-w-2xl text-xs text-muted">{d.definition}</p>
                  </details>
                </td>
                <td className="py-3 px-3 text-right">
                  <label className="sr-only" htmlFor={`w-${d.key}`}>
                    {d.label} weight
                  </label>
                  <input
                    id={`w-${d.key}`}
                    type="number"
                    min={0}
                    max={1}
                    step={0.05}
                    className={cn(fieldClass, 'w-24 text-right [font-variant-numeric:tabular-nums]')}
                    value={weights[d.key]}
                    onChange={(e) => setWeights((w) => ({ ...w, [d.key]: e.target.value }))}
                  />
                </td>
              </tr>
            ))}
            <tr>
              <td className="py-3 px-3 text-right font-(family-name:--font-ui) text-xs uppercase tracking-[0.12em] text-muted">
                Running total (must be 1.0)
              </td>
              <td className="py-3 px-3 text-right">
                <span
                  className={cn(
                    'font-(family-name:--font-ui) font-semibold [font-variant-numeric:tabular-nums]',
                    sumsToOne ? 'text-score-high' : 'text-accent-danger'
                  )}
                >
                  {total.toFixed(3)}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
        {!sumsToOne && (
          <p className="text-sm text-accent-danger">
            Dimension weights must sum to exactly 1.0 — currently {total.toFixed(3)}.
          </p>
        )}
        {!allValid && <p className="text-sm text-accent-danger">Every weight must be a non-negative number.</p>}
      </Card>

      {/* sub-segment weights */}
      <Card>
        <span className="eyebrow">Sub-segment weights</span>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {SUB_SEGMENTS.map((s) => (
            <label key={s} className="flex items-center justify-between gap-3 text-sm">
              <span className="text-muted">{SUB_SEGMENT_LABELS[s]}</span>
              <input
                type="number"
                min={0}
                max={1}
                step={0.05}
                className={cn(fieldClass, 'w-24 text-right [font-variant-numeric:tabular-nums]')}
                value={segWeights[s]}
                onChange={(e) => setSegWeights((w) => ({ ...w, [s]: e.target.value }))}
              />
            </label>
          ))}
        </div>
        <p className="mt-3 text-xs text-muted [font-variant-numeric:tabular-nums]">
          Sub-segment total: {segTotal.toFixed(3)}
        </p>
      </Card>

      {/* must-haves / exclusions */}
      <Card>
        <span className="eyebrow">Must-haves &amp; exclusions</span>
        <div className="mt-3 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-(family-name:--font-ui) text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-muted">
              Must-haves (one per line)
            </span>
            <textarea
              rows={5}
              className={cn(fieldClass, 'resize-y')}
              value={mustHaves}
              onChange={(e) => setMustHaves(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-(family-name:--font-ui) text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-muted">
              Exclusions (one per line)
            </span>
            <textarea
              rows={5}
              className={cn(fieldClass, 'resize-y')}
              value={exclusions}
              onChange={(e) => setExclusions(e.target.value)}
            />
          </label>
        </div>
      </Card>

      {/* why + save */}
      <Card>
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-(family-name:--font-ui) text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-muted">
            Why this change (stored with the version)
          </span>
          <textarea
            rows={2}
            className={cn(fieldClass, 'max-w-2xl resize-y')}
            placeholder="e.g. Upweight aftermarket attach after the service-network review"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
        <div className="mt-4 flex items-center gap-3">
          <Button variant="primary" disabled={!canSave} onClick={onSave}>
            {save.isPending ? 'Saving…' : `Save as new version`}
          </Button>
          {!sumsToOne && <span className="text-xs text-muted">disabled until weights sum to 1.0</span>}
        </div>
        {save.error && <p className="mt-3 text-sm text-accent-danger">{(save.error as Error).message}</p>}
      </Card>
    </>
  );
}

/* ---------- versions list ---------- */

function VersionsList({
  versions,
  isLoading,
  error,
}: {
  versions: RubricDto[] | undefined;
  isLoading: boolean;
  error: Error | null;
}) {
  return (
    <Card>
      <span className="eyebrow">Versions</span>
      {isLoading && <LoadingSkeleton lines={3} />}
      {error && <ErrorState message={error.message} />}
      {versions &&
        (versions.length === 0 ? (
          <p className="mt-2 text-sm italic text-muted opacity-70">No rubric versions recorded.</p>
        ) : (
          <table className="mt-3 w-full text-sm">
            <thead>
              <tr className="border-b-2 border-accent">
                <Th>Version</Th>
                <Th>Created</Th>
                <Th>Dimensions (weights)</Th>
                <Th right>Status</Th>
              </tr>
            </thead>
            <tbody>
              {[...versions]
                .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))
                .map((v) => (
                  <tr key={v.version} className="border-b border-border align-top hover:bg-surface-hover">
                    <td className="py-3 px-3 font-(family-name:--font-ui) font-semibold">{v.version}</td>
                    <td className="py-3 px-3 text-muted [font-variant-numeric:tabular-nums]">
                      {v.createdAt ? fmtDate(v.createdAt) : '—'}
                    </td>
                    <td className="py-3 px-3 text-muted">
                      {v.dimensions.map((d) => `${d.label} ${d.weight}`).join(' · ')}
                    </td>
                    <td className="py-3 px-3 text-right">
                      {v.active ? (
                        <span className="rounded-(--radius-sm) border border-accent px-2 py-0.5 font-(family-name:--font-ui) text-[0.65rem] font-medium uppercase tracking-[0.12em] text-accent">
                          active
                        </span>
                      ) : (
                        <span className="text-xs text-muted">superseded</span>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        ))}
    </Card>
  );
}

function Th({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return (
    <th
      className={`py-2 px-3 text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui) ${right ? 'text-right' : 'text-left'}`}
    >
      {children}
    </th>
  );
}
